/**
 * Value-session gap stats for C Mode dashboard cards (GAP-5 / SPEC-01 Journey 2).
 *
 * Mirrors BatchValueEntry progress rules (brief-X §2.4): an item with
 * ELIGIBLE_WITH_GAPS counts as resolved when every missingAcceptedValues key
 * is flagged unknown for this session (accepted_exceptions).
 */

import type { AcceptedException } from '../../../types/prioritization/itemLifecycle';
import type { PrioritizationSession } from '../../../types/prioritization/session';

export interface ValueSessionGapStats {
  /** Items that still need a value or unknown-flag */
  itemsWithGaps: number;
  totalItems: number;
  /** Items complete (no gaps, or all gaps flagged unknown) */
  itemsComplete: number;
  dueDate?: Date | string;
  /** True when nothing remains for the participant to enter/flag */
  myComplete: boolean;
  /** True when some gapped items are resolved but others remain */
  myInProgress: boolean;
}

export function getValueSessionGapStats(
  session: PrioritizationSession,
  exceptions: AcceptedException[],
): ValueSessionGapStats {
  const itemIds = session.itemIds ?? [];
  const totalItems = itemIds.length;
  const eligibility = session.itemEligibility ?? {};

  const exceptionKeysByItem = new Map<string, Set<string>>();
  for (const exc of exceptions) {
    if (exc.sessionId && exc.sessionId !== session.id) continue;
    const set = exceptionKeysByItem.get(exc.itemId) ?? new Set<string>();
    set.add(exc.attributeKey);
    exceptionKeysByItem.set(exc.itemId, set);
  }

  let itemsStillNeeding = 0;
  let itemsResolvedAmongGaps = 0;
  let anyPartialFlagProgress = false;

  for (const id of itemIds) {
    const elig = eligibility[id];
    if (elig?.status !== 'ELIGIBLE_WITH_GAPS') continue;

    const missing = elig.missingAcceptedValues ?? [];
    const flagged = exceptionKeysByItem.get(id);
    const flaggedCount = missing.filter((key) => flagged?.has(key)).length;
    const resolved = missing.length > 0 && flaggedCount === missing.length;

    if (resolved) {
      itemsResolvedAmongGaps += 1;
    } else {
      itemsStillNeeding += 1;
      if (flaggedCount > 0) anyPartialFlagProgress = true;
    }
  }

  const itemsComplete = totalItems - itemsStillNeeding;
  const dueDate =
    session.scheduledFor
    ?? session.modeConfig?.closesAt
    ?? session.modeConfig?.opensAt;

  const myComplete = itemsStillNeeding === 0 && totalItems > 0;

  return {
    itemsWithGaps: itemsStillNeeding,
    totalItems,
    itemsComplete,
    dueDate,
    myComplete,
    myInProgress:
      !myComplete
      && (itemsResolvedAmongGaps > 0 || anyPartialFlagProgress || itemsComplete > 0),
  };
}
